import { useCallback, useEffect, useRef, useState } from 'react';
import eventMediaService from '../services/eventMediaService';
import { useDialog } from '../context';
import { Btn, Help, TextInput } from './host/kit';

/**
 * Circular progress "donut" (inherits color via currentColor). Determinate when a positive
 * pct is given (fills the ring); otherwise a spinning arc for indeterminate processing.
 */
function Donut({ pct = null, size = 34, stroke = 3 }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const determinate = typeof pct === 'number' && pct > 0;
  const dash = determinate ? (c * Math.min(pct, 100)) / 100 : c * 0.28;
  return (
    <svg
      width={size} height={size} viewBox={`0 0 ${size} ${size}`}
      className={determinate ? '' : 'animate-spin'}
      style={determinate ? undefined : { animationDuration: '0.8s' }}
      aria-hidden="true"
    >
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="currentColor" strokeOpacity="0.25" strokeWidth={stroke} />
      <circle
        cx={size / 2} cy={size / 2} r={r} fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round"
        strokeDasharray={`${dash} ${c}`} transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    </svg>
  );
}

/**
 * EventMediaManager — host-side gallery editor on the create/edit form. Upload extra
 * photos, paste a YouTube/Vimeo link (played in our branded player on the event page),
 * reorder, and remove. The event poster stays its own field; this is the gallery that
 * shows beneath it. Renders inside the outer <form> so everything is type="button".
 *
 * Presentation: the PHOTOS & VIDEO row of the host event handoff
 * (docs/plans/2026-09-host-event-edit/handoff/README.md, step 2) — square tiles, the
 * drop-zone as the last tile, the video-link row under it — built on the `hev-tile*` /
 * `hev-drop` classes of ./host/hostEvent.css inside a `.hev` scope.
 *
 * Props: eventId (null until the draft exists → disabled empty state); `refreshKey` — bump it
 * to re-read the list (the review step's COVER & PREVIEW reorders photos too).
 */
export default function EventMediaManager({ eventId, className = '', refreshKey = 0 }) {
  const { showAlert, showConfirm } = useDialog();
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(!!eventId);
  const [busy, setBusy] = useState(false);
  const [embedUrl, setEmbedUrl] = useState('');
  const [videoUpload, setVideoUpload] = useState(false);
  const [videoBusy, setVideoBusy] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileRef = useRef(null);
  const videoRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!eventId) { setMedia([]); setLoading(false); return; }
    setLoading(true);
    const r = await eventMediaService.list(eventId);
    if (r.success) setMedia(r.data || []);
    setLoading(false);
  }, [eventId]);

  useEffect(() => { refresh(); }, [refresh, refreshKey]);

  // Probe whether host video upload (Cloudflare Stream) is available in this env.
  useEffect(() => {
    (async () => {
      const r = await eventMediaService.capabilities();
      if (r.success && r.data) setVideoUpload(!!r.data.videoUpload);
    })();
  }, []);

  // Poll a pending uploaded video until Cloudflare finishes encoding.
  const pollVideo = useCallback(async (mediaId) => {
    for (let i = 0; i < 60; i++) { // ~5 min max
      // eslint-disable-next-line no-await-in-loop
      await new Promise((res) => setTimeout(res, 5000));
      // eslint-disable-next-line no-await-in-loop
      const r = await eventMediaService.refresh(mediaId);
      if (r.success && r.data) {
        setMedia((prev) => prev.map((m) => (m.id === mediaId ? r.data : m)));
        if (r.data.status === 'error') return;
        // Keep polling a "ready" video until its thumbnail exists — Cloudflare can flip to ready a
        // beat before the poster frame is generated, which would otherwise leave a black tile.
        if (r.data.status === 'ready' && r.data.thumbnailUrl) return;
      }
    }
  }, []);

  const [uploadPct, setUploadPct] = useState(0);
  const onPickVideo = async (e) => {
    const file = (e.target.files || [])[0];
    if (!file || !eventId) return;
    if (file.size > 200 * 1024 * 1024) {
      if (videoRef.current) videoRef.current.value = '';
      await showAlert({ title: 'Too large', message: 'Videos must be under 200MB. Trim it or lower the resolution.', type: 'error' });
      return;
    }
    setVideoBusy(true);
    setUploadPct(0);
    // Upload through our server (forwards to Cloudflare) — reliable on networks that
    // block direct browser→Cloudflare uploads.
    const r = await eventMediaService.uploadVideo(eventId, file, setUploadPct);
    setVideoBusy(false);
    if (videoRef.current) videoRef.current.value = '';
    if (!r.success || !r.data) {
      await showAlert({ title: 'Upload failed', message: r.error || 'Try again.', type: 'error' });
      return;
    }
    setMedia((prev) => [...prev, r.data]); // pending tile
    pollVideo(r.data.id); // encode in the background; tile updates when ready
  };

  const uploadImages = async (files) => {
    const imgs = files.filter((f) => f.type.startsWith('image/'));
    if (!imgs.length || !eventId) return;
    setBusy(true);
    // Upload sequentially to keep order deterministic.
    for (const f of imgs) {
      const r = await eventMediaService.addImage(eventId, f);
      if (r.success && r.data) setMedia((prev) => [...prev, r.data]);
      else await showAlert({ title: 'Upload failed', message: r.error || 'Try again.', type: 'error' });
    }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const onPickFiles = (e) => uploadImages(Array.from(e.target.files || []));
  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    uploadImages(Array.from(e.dataTransfer?.files || []));
  };

  const addEmbed = async () => {
    const url = embedUrl.trim();
    if (!url || !eventId) return;
    setBusy(true);
    const r = await eventMediaService.addEmbed(eventId, url);
    setBusy(false);
    if (r.success && r.data) { setMedia((prev) => [...prev, r.data]); setEmbedUrl(''); }
    else await showAlert({ title: 'Could not add video', message: r.error || 'Paste a YouTube or Vimeo link.', type: 'error' });
  };

  const remove = async (m) => {
    const ok = await showConfirm({ title: 'REMOVE MEDIA', message: 'Remove this item from the gallery?', confirmLabel: 'REMOVE' });
    if (!ok) return;
    const r = await eventMediaService.remove(m.id);
    if (r.success) setMedia((prev) => prev.filter((x) => x.id !== m.id));
  };

  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= media.length) return;
    const next = [...media];
    [next[i], next[j]] = [next[j], next[i]];
    setMedia(next);
    await eventMediaService.reorder(eventId, next.map((m) => m.id));
  };

  const uploadLinkStyle = { border: 'none', background: 'none', padding: 0, font: 'inherit', fontWeight: 700, letterSpacing: 'inherit', color: '#000', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, textTransform: 'uppercase' };

  return (
    <div className={`hev-field ${className}`} style={{ gap: 10 }}>
      <span className="hev-label">PHOTOS &amp; VIDEO</span>

      {!eventId && (
        <Help>Save the event basics first (title + date) — the gallery unlocks once your draft exists.</Help>
      )}

      {eventId && (
        <>
          {loading && <Help>Loading…</Help>}

          <div className="hev-tiles">
            {!loading && media.map((m, i) => {
              const isVideo = m.kind === 'video' || m.kind === 'embed';
              const pending = m.kind === 'video' && m.status === 'pending';
              const playable = m.kind === 'embed' || (m.kind === 'video' && m.status === 'ready');
              return (
                <div key={m.id} className="hev-tile">
                  {(m.thumbnailUrl || m.url) && <img src={m.thumbnailUrl || m.url} alt="" referrerPolicy="no-referrer" />}
                  {i === 0 && <span className="hev-tile-badge" data-lead>LEAD</span>}
                  {isVideo && <span className="hev-tile-badge" data-video>VIDEO</span>}
                  {pending && (
                    <span className="hev-tile-veil">
                      <span style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Donut pct={typeof m.processingPct === 'number' ? m.processingPct : null} size={38} />
                        {typeof m.processingPct === 'number' && (
                          <span style={{ position: 'absolute', fontSize: 9, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{m.processingPct}%</span>
                        )}
                      </span>
                      <span>PROCESSING</span>
                    </span>
                  )}
                  {playable && <span className="hev-tile-play">▶</span>}
                  <span className="hev-tile-ctl">
                    <span>
                      <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move left">◄</button>
                      <button type="button" onClick={() => move(i, 1)} disabled={i === media.length - 1} aria-label="Move right">►</button>
                    </span>
                    <button type="button" onClick={() => remove(m)} data-x aria-label="Remove">✕</button>
                  </span>
                </div>
              );
            })}

            {/* Photo dropzone — the last tile; click or drag, mirrors the poster upload. */}
            <input ref={fileRef} type="file" accept="image/*" multiple onChange={onPickFiles} disabled={busy} style={{ display: 'none' }} />
            <button
              type="button"
              className="hev-drop"
              data-square
              data-active={dragActive ? '' : undefined}
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={() => setDragActive(false)}
              onDrop={onDrop}
              disabled={busy}
            >
              <b>+</b>
              <span>{busy ? 'UPLOADING…' : 'ADD PHOTOS'}</span>
            </button>
          </div>

          {/* Video: paste a link OR upload a file (the link shows a donut with upload %). */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <TextInput
              size="sm"
              value={embedUrl}
              onChange={(e) => setEmbedUrl(e.target.value)}
              placeholder="Add a video link — youtube.com/… or vimeo.com/…"
              aria-label="Video link"
              style={{ flex: '1 1 240px', width: 'auto' }}
            />
            <Btn onClick={addEmbed} disabled={busy || !embedUrl.trim()}>ADD VIDEO</Btn>
          </div>

          <span className="hev-caps-muted" style={{ fontWeight: 500, letterSpacing: '.12em' }}>
            {videoUpload ? (
              <>
                <input ref={videoRef} type="file" accept="video/*" onChange={onPickVideo} disabled={videoBusy} style={{ display: 'none' }} />
                OR{' '}
                <button type="button" onClick={() => videoRef.current?.click()} disabled={videoBusy} style={uploadLinkStyle}>
                  {videoBusy
                    ? (<><Donut pct={uploadPct} size={14} stroke={2} /> UPLOADING {uploadPct}%</>)
                    : 'UPLOAD A FILE'}
                </button>
                {' '}· THE FIRST ONE LEADS THE GALLERY
              </>
            ) : 'THE FIRST ONE LEADS THE GALLERY'}
          </span>

          {videoUpload && (
            <Help>
              Up to 200MB. It processes after upload — you can save and leave; it’ll be added to
              your event automatically once it’s ready.
            </Help>
          )}
        </>
      )}
    </div>
  );
}
